/* eslint-disable max-lines */
import React, { useState } from 'react';
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import NumberFormat from 'react-number-format';
import InputMask from 'react-input-mask';
import SignatureCanvas from 'react-signature-canvas';

interface NC4StepOneProps {
  formData: {
    totalAllowances: string;
    additionalAmount: string;
    socialSecurityNumber: string;
    filingStatus: 'single_married_separately' | 'head_of_household' | 'married_jointly_surviving_spouse' | '';
    firstName: string;
    middleInitial: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handleNumericInputChange: (name: string) => (values: any) => void;
  handleRadioChange: (name: string, value: string) => void;
  setSignature: (value: any) => void;
  signature: any;
  states: { id: string; name: string; uf: string }[];
  loadingStates: boolean;
}

const NC4StepOne: React.FC<NC4StepOneProps> = ({
  formData,
  handleInputChange,
  handleNumericInputChange,
  handleRadioChange,
  setSignature,
  states,
  loadingStates,
}) => {
  const [sigPad, setSigPad] = useState<any>();

  const getSignature = () => {
    if (sigPad) {
      const img = sigPad.getTrimmedCanvas()?.toDataURL('image/png');
      if (img) {
        setSignature(img);
      }
    }
  };

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col md="9">
          <Label htmlFor="nc4TotalAllowances" className="mb-0" style={{ fontSize: '0.9rem' }}>
            1. Total number of allowances you are claiming
            <br />
            <small className="text-muted">
              (Enter zero (0), or the number of allowances from Page 2, Line 17 of the NC-4 Allowance Worksheet)
            </small>
          </Label>
        </Col>
        <Col md="3">
          <NumberFormat
            name="totalAllowances"
            id="nc4TotalAllowances"
            className="form-control text-right"
            value={formData.totalAllowances}
            onValueChange={handleNumericInputChange('totalAllowances')}
            thousandSeparator={false}
            allowNegative={false}
            decimalScale={0}
            placeholder="0"
            required
          />
        </Col>
      </Row>

      <Row className="align-items-center mb-4">
        <Col md="9">
          <Label htmlFor="nc4AdditionalAmount" className="mb-0" style={{ fontSize: '0.9rem' }}>
            2. Additional amount, if any, you want withheld from each pay period
            <br />
            <small className="text-muted">(Enter whole dollars)</small>
          </Label>
        </Col>
        <Col md="3" className="d-flex align-items-center">
          <NumberFormat
            name="additionalAmount"
            id="nc4AdditionalAmount"
            className="form-control text-right"
            value={formData.additionalAmount}
            onValueChange={handleNumericInputChange('additionalAmount')}
            thousandSeparator={true}
            decimalScale={0}
            allowNegative={false}
            placeholder="0"
            suffix=".00"
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md="4">
          <FormGroup>
            <Label htmlFor="nc4SocialSecurityNumber">Social Security Number *</Label>
            <Input
              type="text"
              name="socialSecurityNumber"
              id="nc4SocialSecurityNumber"
              value={formData.socialSecurityNumber}
              onChange={handleInputChange}
              tag={InputMask}
              mask="999-99-9999"
              placeholder="___-__-____"
              required
            />
          </FormGroup>
        </Col>
        <Col md="8">
          <FormGroup>
            <Label>Filing Status *</Label>
            <div className="d-flex flex-wrap mt-1">
              {' '}
              <FormGroup check inline className="mr-3 mb-1">
                <Input
                  type="radio"
                  name="filingStatus"
                  id="nc4StatusSingle"
                  value="single_married_separately"
                  checked={formData.filingStatus === 'single_married_separately'}
                  onChange={(e) => handleRadioChange('filingStatus', e.target.value)}
                  required
                />
                <Label check htmlFor="nc4StatusSingle" style={{ fontSize: '0.85rem' }}>
                  Single or Married Filing Separately
                </Label>
              </FormGroup>
              <FormGroup check inline className="mr-3 mb-1">
                <Input
                  type="radio"
                  name="filingStatus"
                  id="nc4StatusHoH"
                  value="head_of_household"
                  checked={formData.filingStatus === 'head_of_household'}
                  onChange={(e) => handleRadioChange('filingStatus', e.target.value)}
                />
                <Label check htmlFor="nc4StatusHoH" style={{ fontSize: '0.85rem' }}>
                  Head of Household
                </Label>
              </FormGroup>
              <FormGroup check inline className="mb-1">
                <Input
                  type="radio"
                  name="filingStatus"
                  id="nc4StatusMarriedJointly"
                  value="married_jointly_surviving_spouse"
                  checked={formData.filingStatus === 'married_jointly_surviving_spouse'}
                  onChange={(e) => handleRadioChange('filingStatus', e.target.value)}
                />
                <Label check htmlFor="nc4StatusMarriedJointly" style={{ fontSize: '0.85rem' }}>
                  Married Filing Jointly or Surviving Spouse
                </Label>
              </FormGroup>
            </div>
          </FormGroup>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md="5">
          <FormGroup>
            <Label htmlFor="nc4FirstName">
              First Name <small className="text-muted">(USE CAPITAL LETTERS FOR YOUR NAME AND ADDRESS)</small> *
            </Label>
            <Input
              type="text"
              name="firstName"
              id="nc4FirstName"
              value={formData.firstName}
              onChange={handleInputChange}
              style={{ textTransform: 'uppercase' }}
              required
            />
          </FormGroup>
        </Col>
        <Col md="2">
          <FormGroup>
            <Label htmlFor="nc4MiddleInitial">M.I. *</Label>
            <Input
              type="text"
              name="middleInitial"
              id="nc4MiddleInitial"
              value={formData.middleInitial}
              onChange={handleInputChange}
              maxLength={1}
              style={{ textTransform: 'uppercase' }}
            />
          </FormGroup>
        </Col>
        <Col md="5">
          <FormGroup>
            <Label htmlFor="nc4LastName">Last Name *</Label>
            <Input
              type="text"
              name="lastName"
              id="nc4LastName"
              value={formData.lastName}
              onChange={handleInputChange}
              style={{ textTransform: 'uppercase' }}
              required
            />
          </FormGroup>
        </Col>
      </Row>

      <FormGroup className="mb-3">
        <Label htmlFor="nc4Address">Address *</Label>
        <Input
          type="text"
          name="address"
          id="nc4Address"
          value={formData.address}
          onChange={handleInputChange}
          style={{ textTransform: 'uppercase' }}
          required
        />
      </FormGroup>

      <Row className="mb-3">
        <Col md="4">
          <FormGroup>
            <Label htmlFor="nc4City">City *</Label>
            <Input
              type="text"
              name="city"
              id="nc4City"
              value={formData.city}
              onChange={handleInputChange}
              style={{ textTransform: 'uppercase' }}
              required
            />
          </FormGroup>
        </Col>
        <Col md="3">
          <FormGroup>
            <Label htmlFor="nc4State">State *</Label>
            <Input
              type="select"
              name="state"
              id="nc4State"
              value={formData.state}
              onChange={handleInputChange}
              required
              disabled={loadingStates}
            >
              <option value="">Select State...</option>
              {loadingStates ? (
                <option disabled>Loading states...</option>
              ) : (
                states.map((stateOpt) => (
                  <option key={stateOpt.uf} value={stateOpt.uf}>
                    {stateOpt.name}
                  </option>
                ))
              )}
            </Input>
          </FormGroup>
        </Col>
        <Col md="2">
          <FormGroup>
            <Label htmlFor="nc4ZipCode">
              Zip Code <small className="text-muted">(5 Digit)</small> *
            </Label>
            <Input
              type="text"
              name="zipCode"
              id="nc4ZipCode"
              value={formData.zipCode}
              onChange={handleInputChange}
              maxLength={5}
              pattern="\d{5}"
              title="Enter a 5-digit ZIP code"
              required
            />
          </FormGroup>
        </Col>
        <Col md="3">
          <FormGroup>
            <Label htmlFor="nc4Country">
              Country <small className="text-muted">(if not U.S.)</small>
            </Label>
            <Input
              type="text"
              name="country"
              id="nc4Country"
              value={formData.country}
              onChange={handleInputChange}
              style={{ textTransform: 'uppercase' }}
            />
          </FormGroup>
        </Col>
        <Col md="12">
          <FormGroup>
            <Row>
              <Col md="12">
                <Label for="employeeSignature">Employee's Signature *</Label>
              </Col>
              <Col md="12">
                <SignatureCanvas
                  penColor="blue"
                  canvasProps={{ width: 500, height: 200, className: 'sig-canvas' }}
                  ref={(ref) => setSigPad(ref)}
                  onEnd={getSignature}
                />
              </Col>
              <Col md="12">
                <Button
                  onClick={() => {
                    sigPad?.clear();
                  }}
                  color="warning"
                  size="sm"
                  className="mt-1 mb-2"
                >
                  Clear
                </Button>
              </Col>
            </Row>
          </FormGroup>
        </Col>
      </Row>
    </>
  );
};

export default NC4StepOne;
